import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DataTable from '@/components/DataTable/DataTable';

const mapSupplierToRefundFormPatch = (supplier) => {
  if (supplier == null) return {};
  if (typeof supplier === 'string') {
    return { vendorIdNumber: supplier };
  }
  const addressLine2 = [supplier.addressLine2, supplier.addressLine3]
    .map((s) => (s != null ? String(s).trim() : ''))
    .filter(Boolean)
    .join(', ');

  return {
    vendorIdNumber: supplier.id ?? '',
    businessPayeeName: supplier.name || '',
    payeeAccountNumber: supplier.supplierNumber ?? '',
    email: supplier.email ?? '',
    phone: supplier.phoneNumber ?? '',
    refundToAddressLine1: supplier.addressLine1 ?? '',
    refundToAddressLine2: addressLine2,
    city: supplier.city ?? '',
    state: supplier.state || supplier.province || '',
    refundToZip: supplier.zip ?? '',
    refundToCountry: supplier.countryCode ?? '',
  };
};

const SearchSuppliersModal = ({ open, onClose, onSelectSupplier }) => {
  const { t } = useTranslation();
  const [supplierSearchInput, setSupplierSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const canSearch = searchTerm.trim().length >= 3;

  const columns = useMemo(
    () => [
      { key: 'id', label: 'ID' },
      { key: 'name', label: t('createPayment.suppliersModal.name') },
      {
        key: 'supplierNumber',
        label: t('createPayment.suppliersModal.supplierNumber'),
      },
      { key: 'site', label: t('createPayment.suppliersModal.site') },
      {
        key: 'phoneNumber',
        label: t('createPayment.suppliersModal.phoneNumber'),
      },
      { key: 'email', label: t('createPayment.suppliersModal.email') },
      {
        key: 'addressLine1',
        label: t('createPayment.suppliersModal.addressLine1'),
      },
      {
        key: 'addressLine2',
        label: t('createPayment.suppliersModal.addressLine2'),
      },
      {
        key: 'addressLine3',
        label: t('createPayment.suppliersModal.addressLine3'),
      },
      { key: 'province', label: t('createPayment.suppliersModal.province') },
      { key: 'city', label: t('createPayment.suppliersModal.city') },
      { key: 'state', label: t('createPayment.suppliersModal.state') },
      { key: 'zip', label: t('createPayment.suppliersModal.zip') },
      { key: 'countryCode', label: t('createPayment.suppliersModal.country') },
    ],
    [t],
  );

  useEffect(() => {
    if (!open) {
      setSupplierSearchInput('');
      setSearchTerm('');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (supplierSearchInput.length >= 3) {
      setError(null);
    }
  }, [supplierSearchInput]);

  const executeSearch = () => {
    if (supplierSearchInput.length < 3) {
      setError(t('createPayment.suppliersModal.minCharsHint'));
      return;
    }
    setError(null);
    setSearchTerm(supplierSearchInput.trim());
  };

  const handleClose = () => {
    onClose();
  };

  const handleSelectSupplier = (item) => {
    const patch = mapSupplierToRefundFormPatch(item);
    if (!patch.vendorIdNumber) return;
    onSelectSupplier(patch);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='xl'>
      <DialogTitle>{t('createPayment.suppliersModal.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            autoFocus
            margin='dense'
            label={t('createPayment.suppliersModal.searchLabel')}
            placeholder={t('createPayment.suppliersModal.searchPlaceholder')}
            fullWidth
            variant='standard'
            error={Boolean(error)}
            helperText={error}
            value={supplierSearchInput}
            onChange={(e) => setSupplierSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                executeSearch();
              }
            }}
          />
          <Button
            variant='contained'
            sx={{ mt: 2 }}
            disabled={supplierSearchInput.length < 3}
            onClick={executeSearch}>
            {t('createPayment.suppliersModal.searchLabel')}
          </Button>
        </Box>
        <Box sx={{ mt: 2 }}>
          <DataTable
            key={canSearch ? `suppliers-${searchTerm}` : 'suppliers-idle'}
            columns={columns}
            endpoint={canSearch ? '/payments/suppliers' : undefined}
            enableSorting={false}
            enableColumnPicker={false}
            pageSize={25}
            onRowClick={handleSelectSupplier}
            initialFilters={canSearch ? { supplierName: searchTerm } : {}}
            tableProps={{
              sx: {
                maxHeight: 360,
              },
            }}
            renderEmptyState={() => (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align='center'
                  sx={{ py: 5 }}>
                  <Typography variant='body2' color='text.secondary'>
                    {t('createPayment.suppliersModal.noResults')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {t('createPayment.suppliersModal.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SearchSuppliersModal;
